import React, { useEffect, useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import bombIcon from "../assets/images/bomb.svg";
import CloseIcon from "@mui/icons-material/Close";
import { Website } from "../types/siteSpecs";
import { SITE_URL, withoutHttpUrl } from "../siteUrl";
import { trackEvent } from "../analytics";
import { GOOGLE_APPS_SCRIPT_WEB_APP_URL } from "../googleAppsScriptWebApp";
import AnimatedRainbowBorder from "./AnimatedRainbowBorder";

interface LastVisitedPopupProps {
  website: Website;
  onClose: () => void;
}

const LastVisitedPopup: React.FC<LastVisitedPopupProps> = ({
  website,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [tellOthersCopied, setTellOthersCopied] = useState(false);
  const [reported, setReported] = useState(false);
  const [reportError, setReportError] = useState("");
  const [stickerIn, setStickerIn] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    if (!isVisible || !website.submittedBy) return;
    const t = setTimeout(() => setStickerIn(true), 480);
    return () => clearTimeout(t);
  }, [isVisible, website.submittedBy]);

  // Same rhythm as SubmitLinkPopUp: thank-you state, then close.
  useEffect(() => {
    if (!reported) return;
    const id = window.setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 340);
    }, 2000);
    return () => clearTimeout(id);
  }, [reported, onClose]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 340);
  };

  const truncateUrl = (url: string) => {
    const maxLength = 34;
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + "...";
  };

  const handleCopy = () => {
    trackEvent("last_visited_copy_destination_url");
    navigator.clipboard.writeText(withoutHttpUrl(website.url)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }).catch(() => {
      setCopied(false);
    });
  };

  const tellOthersClipboardText = `(⌐⊙o⊙)

How cool is this site - ${withoutHttpUrl(website.url)}


o()xxxx[{::::::::::::::::::>

Found it on ${withoutHttpUrl(SITE_URL)}

<::::::::::::::::::[{xxxx()o`;

  const handleTellOthersCopy = () => {
    trackEvent("last_visited_tell_others_copy");
    navigator.clipboard.writeText(tellOthersClipboardText).then(() => {
      setTellOthersCopied(true);
      setTimeout(() => setTellOthersCopied(false), 2000);
    }).catch(() => {
      setTellOthersCopied(false);
    });
  };

  const handleReport = async () => {
    if (reported) return;
    trackEvent("last_visited_report");
    setReportError("");
    try {
      await fetch(GOOGLE_APPS_SCRIPT_WEB_APP_URL, {
        method: "POST",
        mode: "no-cors",
        body: JSON.stringify({
          formType: "report",
          url: website.url,
          timestamp: new Date().toISOString(),
        }),
        headers: { "Content-Type": "text/plain" },
      });
      setReported(true);
    } catch {
      setReportError("Couldn't send report. Try again.");
    }
  };

  return (
    <div
      className="fixed z-50 transition-all duration-[340ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
      style={{
        bottom: 24,
        right: 24,
        width: 320,
        transform: isVisible
          ? "translate3d(0, 0, 0) scale(1)"
          : "translate3d(26%, 0, 0) scale(1)",
        opacity: 1,
        transformOrigin: "bottom right",
      }}
    >
      {/* Sticker — sits outside the card, overlapping the top edge */}
      {website.submittedBy && (
        <div
          className="absolute z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full"
          style={{
            top: -14,
            left: 18,
            background: "#000",
            border: "1.5px solid rgba(255,255,255,0.15)",
            boxShadow: "0 4px 16px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)",
            transform: stickerIn
              ? "rotate(-2deg) scale(1)"
              : "rotate(-8deg) scale(0.5) translateY(12px)",
            opacity: stickerIn ? 1 : 0,
            transition: "transform 550ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 350ms ease-out",
          }}
        >
          <AutoAwesomeRoundedIcon sx={{ fontSize: 14, color: "#FEFF9D" }} />
          <span className="gilroy-semibold text-[13px] text-white/70 leading-none whitespace-nowrap">
            Discovered by{" "}
            <span className="gilroy-bold text-[14px] text-white">{website.submittedBy}</span>
          </span>
        </div>
      )}

      <AnimatedRainbowBorder
        borderRadius={16}
        borderWidth={2}
        speed="6s"
        glowOpacity={0.7}
        contentClassName={reported ? "p-6 min-h-[200px]" : "p-6"}
        className="gilroy-medium"
      >
        {reported ? (
          <div className="h-full min-h-[168px] flex flex-col">
            <div className="flex justify-end shrink-0">
              <button
                type="button"
                onClick={handleClose}
                className="text-black rounded-full h-[24px] w-[24px] flex items-center justify-center border border-black shrink-0 hover:bg-black hover:text-white transition-colors"
                aria-label="Close"
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </button>
            </div>
            <div className="flex-1 flex items-center justify-center px-2 -mt-2">
              <div className="text-black gilroy-bold text-center text-xl leading-snug">
                You are a hero, <br />
                cupcakes!
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-start mb-6">
              <div
                className="text-[18px] text-black leading-tight"
                style={{ fontFamily: "'Kurdis Bold'" }}
              >
                You just went
                <br />
                somewhere cool.
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="text-black rounded-full h-[24px] w-[24px] flex items-center justify-center border border-black shrink-0 ml-2 hover:bg-black hover:text-white transition-colors"
                aria-label="Close"
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </button>
            </div>

            <div className="mb-6">
              <p className="gilroy-bold text-[16px] text-black mb-1 line-clamp-2 leading-snug">
                {website.title}
              </p>

              <div className="flex items-center gap-2">
                <a
                  href={website.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gilroy-regular text-[14px] text-[#555] underline flex-1 min-w-0 truncate hover:text-black transition-colors"
                  title={website.url}
                >
                  {truncateUrl(website.url)}
                </a>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="text-[#343434] hover:opacity-60 flex-shrink-0 transition-opacity"
                  title="Copy URL"
                >
                  {copied ? (
                    <CheckRoundedIcon sx={{ fontSize: 17 }} />
                  ) : (
                    <ContentCopyIcon sx={{ fontSize: 17 }} />
                  )}
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-4 flex-wrap">
                <button
                  type="button"
                  onClick={handleTellOthersCopy}
                  className="flex items-center gap-1.5 bg-black text-white gilroy-medium text-[14px] px-4 py-2.5 rounded-[8px] hover:opacity-80 transition-opacity border-0 cursor-pointer"
                >
                  {tellOthersCopied ? (
                    <CheckRoundedIcon sx={{ fontSize: 16, color: "#fff" }} />
                  ) : (
                    <img src={bombIcon} alt="" style={{ width: 16, height: 16, filter: "invert(1)" }} />
                  )}
                  {tellOthersCopied ? "Copied!" : "Tell others"}
                </button>
                <button
                  type="button"
                  onClick={handleReport}
                  className="flex items-center gap-1 gilroy-medium text-[13px] transition-colors cursor-pointer"
                  style={{ color: "#d97706" }}
                  onMouseEnter={e => (e.currentTarget.style.color = "#b45309")}
                  onMouseLeave={e => (e.currentTarget.style.color = "#d97706")}
                >
                  <FlagOutlinedIcon sx={{ fontSize: 15 }} />
                  Report website
                </button>
              </div>
              {reportError ? (
                <p className="gilroy-regular text-[13px] text-red-600">{reportError}</p>
              ) : null}
            </div>
          </>
        )}
      </AnimatedRainbowBorder>
    </div>
  );
};

export default LastVisitedPopup;
